import React, { useEffect, useState } from 'react';
import { getLabourPaymentList } from '../../../helper/Api';
import DataTable from 'react-data-table-component';
import { AiOutlineEye, AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { AddCommasToAmount } from '../../../helper/Utils';
import '../Royalty/RoyaltyApprovalList.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`royalty-approval-col-header royalty-approval-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="royalty-approval-cell-center">{children}</div>
);

const getApprovalBadgeClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'bg-warning-20';
    }
    const val = Number(approval);
    if (val === 1) return 'bg-success-20';
    if (val === 0 || val === 2) return 'bg-danger-20';
    return 'bg-warning-20';
};

const getApprovalIconClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'icon-warning_amber text-warning-100';
    }
    const val = Number(approval);
    if (val === 1) return 'icon-verified text-success-100';
    if (val === 0 || val === 2) return 'icon-warning_amber text-danger-100';
    return 'icon-warning_amber text-warning-100';
};

const PaymentStatusCell = ({ l2Approval, l3Approval }) => (
    <div className="royalty-approval-status-cell">
        <span className={`royalty-approval-status-badge ${getApprovalBadgeClass(l2Approval)}`}>
            L2 <i className={`${getApprovalIconClass(l2Approval)} fs-16px`} />
        </span>
        <span className={`royalty-approval-status-badge ${getApprovalBadgeClass(l3Approval)}`}>
            L3 <i className={`${getApprovalIconClass(l3Approval)} fs-16px`} />
        </span>
    </div>
);

const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};

const LabourCessPaymentList = () => {
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [data, setData] = useState([]);

    const fetchChallans = async () => {
        try {
            const response = await getLabourPaymentList();
            if (response.data.status) {
                setData(response.data.result);
            }
        } catch (error) {
            setData([]);
        }
    };

    useEffect(() => {
        fetchChallans();
    }, []);

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 400,
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            name: 'Actions',
            selector: (row) => (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ gap: '6px', width: '100%' }}
                >
                    <Link to={`/view-labourCess/${row.payment_order_no}`} title="View Details">
                        <button
                            type="button"
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                cursor: 'pointer',
                            }}
                        >
                            <AiOutlineEye size={16} />
                        </button>
                    </Link>
                    <button
                        type="button"
                        title="View Document"
                        disabled={!row?.doc_url}
                        onClick={() => handleDownload(row.doc_url)}
                        style={{
                            ...actionIconBtnStyle,
                            border: '1px solid #f59e0b',
                            color: '#f59e0b',
                            cursor: row?.doc_url ? 'pointer' : 'not-allowed',
                            opacity: row?.doc_url ? 1 : 0.5,
                        }}
                    >
                        <AiOutlineVerticalAlignBottom size={16} />
                    </button>
                </div>
            ),
            width: '110px',
            minWidth: '110px',
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Payment Order', 'Details']} align="left" />,
            selector: (row) => {
                const orderDate = row.payment_order_date
                    ? moment(row.payment_order_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.payment_order_no || ''}</b></div>
                        {orderDate && <div className="text-muted fs-12px">{orderDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '150px',
            grow: 1.5,
        },
        {
            name: <MultiLineHeader lines={['Authority', 'Details']} align="left" />,
            selector: (row) => (
                <div>
                    <div><b>{row.name || ''}</b></div>
                    <div className="text-muted fs-12px">{row.account_no || ''}</div>
                    {row.ifsc_code && (
                        <div className="text-muted fs-12px">IFSC: {row.ifsc_code}</div>
                    )}
                </div>
            ),
            wrap: true,
            minWidth: '200px',
            grow: 2,
        },
        {
            name: <MultiLineHeader lines={['Challan', 'Amount']} align="center" />,
            selector: (row) => (
                <CenterCell>
                    {row.amount ? AddCommasToAmount(row.amount) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '120px',
            grow: 1,
        },
        {
            name: 'Payment Status',
            selector: (row) => (
                <PaymentStatusCell
                    l2Approval={row.l2_payment_approval}
                    l3Approval={row.l3_payment_approval}
                />
            ),
            center: true,
            minWidth: '170px',
            grow: 1.2,
        },
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
    };

    return (
        <div className="royalty-approval-datatable-root">
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={data}
                fixedHeader
                fixedHeaderScrollHeight="600px"
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={data.length}
                paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </div>
    );
};

export default LabourCessPaymentList;
